import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  BookMarked,
  Plus,
  Pencil,
  Trash2,
  Sparkles,
  Loader2,
  ChevronRight,
  User,
  MapPin,
  BookOpen,
  Package,
  Building2,
  FileText,
  Image as ImageIcon,
  Palette,
  ScanText,
  ChevronDown,
} from 'lucide-react';
import { PageHeader } from './ui/page-header';
import { EmptyState } from './ui/empty-state';
import { api, AiLimitError, type BibleEntry, type BibleEntryType, type CreateBibleEntryData, type MangaPage } from '../lib/api';
import { toast } from '../lib/toast';
import { cn } from '../lib/utils';
import { EntryDossier } from './entry-dossier';
import { Rise } from './ui/rise';
import { useAppStore } from '../stores/app-store';
import { BookCover } from './book-cover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { StylePicker } from './style-picker';
import { EntryImageStudio } from './entry-image-studio';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
} from './ui/responsive-modal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

// ============================================
// Constants
// ============================================

const ENTRY_TYPE_ICONS: Record<BibleEntryType, typeof User> = {
  character: User,
  location: MapPin,
  lore: BookOpen,
  item: Package,
  organization: Building2,
  note: FileText,
};

// uni-av / uni-type do mock — gradiente e chip por tipo
const TYPE_GRADIENT: Record<string, string> = {
  character: 'linear-gradient(135deg,#7EA4F5,#5B8DEF)',
  location: 'linear-gradient(135deg,var(--aurora),#2E9E8A)',
  lore: 'linear-gradient(135deg,#C4ABFC,#A78BFA)',
  item: 'linear-gradient(135deg,var(--ember-hot),var(--ember))',
  organization: 'linear-gradient(135deg,#A8B8E8,#6B7BA8)',
  note: 'linear-gradient(135deg,#8B9AC8,#5C6890)',
};
const TYPE_CHIP: Record<string, string> = {
  character: 'text-[#9DBCF9] bg-[rgba(91,141,239,0.12)] ring-[rgba(91,141,239,0.35)]',
  location: 'text-[#7FE7C2] bg-[rgba(94,234,212,0.1)] ring-[rgba(94,234,212,0.35)]',
  lore: 'text-[#C9B4FD] bg-[rgba(167,139,250,0.12)] ring-[rgba(167,139,250,0.35)]',
  item: 'text-[color:var(--ember-hot)] bg-[rgba(223,201,147,0.1)] ring-[rgba(223,201,147,0.35)]',
  organization: 'text-[#A8B8E8] bg-[rgba(168,184,232,0.08)] ring-[rgba(168,184,232,0.3)]',
  note: 'text-muted-foreground bg-foreground/5 ring-border',
};

const ALL_TYPES: BibleEntryType[] = ['character', 'location', 'lore', 'item', 'organization', 'note'];

/** Translated label for a bible entry type. */
function getEntryTypeLabel(t: TFunction, type: BibleEntryType): string {
  switch (type) {
    case 'character':
      return t('storyBible.entryTypeCharacter', 'Personagem');
    case 'location':
      return t('storyBible.entryTypeLocation', 'Local');
    case 'lore':
      return t('storyBible.entryTypeLore', 'Lore');
    case 'item':
      return t('storyBible.entryTypeItem', 'Item');
    case 'organization':
      return t('storyBible.entryTypeOrganization', 'Organização');
    case 'note':
      return t('storyBible.entryTypeNote', 'Nota');
    default:
      return type;
  }
}

// ============================================
// EntryModal — create/edit
// ============================================

interface EntryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry?: BibleEntry | null;
  defaultType?: BibleEntryType;
  projectId: string;
  onSave: (data: CreateBibleEntryData) => Promise<void>;
  /** Called when "Criar/expandir com IA" auto-created related Story Bible stubs,
   * so the parent list (Universo) can refresh. */
  onExternalEntriesAdded?: () => void;
}

function EntryModal({ open, onOpenChange, entry, defaultType = 'character', projectId, onSave, onExternalEntriesAdded }: EntryModalProps) {
  const { t } = useTranslation();
  const [type, setType] = useState<BibleEntryType>(entry?.type ?? defaultType);
  const [name, setName] = useState(entry?.name ?? '');
  const [content, setContent] = useState(entry?.content ?? '');
  const [attrKey, setAttrKey] = useState('');
  const [attrVal, setAttrVal] = useState('');
  const [attributes, setAttributes] = useState<Record<string, string>>(entry?.attributes ?? {});
  const [saving, setSaving] = useState(false);
  const [expanding, setExpanding] = useState(false);

  // Expand the seed (name + whatever's in the description) into a rich, canonical
  // entry via AI — fleshes out depth consistent with the premise/outline/cast.
  const handleExpand = async () => {
    if (!name.trim()) { toast.error(t('storyBible.expandNeedName', 'Dê um nome primeiro.')); return; }
    setExpanding(true);
    try {
      const res = await api.expandBibleEntry(projectId, { type, name: name.trim(), seed: content.trim() });
      if (res.content) setContent(res.content);
      if (res.attributes && Object.keys(res.attributes).length > 0) {
        setAttributes((prev) => ({ ...prev, ...res.attributes }));
      }
      toast.success(t('storyBible.expandDone', 'Ficha enriquecida pela IA — revise e salve.'));
      if (res.relatedAdded && res.relatedAdded.length > 0) {
        toast.success(t('storyBible.relatedAdded', { count: res.relatedAdded.length, defaultValue: '{{count}} relacionado(s) adicionado(s) ao Universo.' }));
        onExternalEntriesAdded?.();
      }
    } catch (e) {
      toast.error(e instanceof AiLimitError
        ? t('storyBible.expandLimit', 'Limite de ações de IA atingido este mês.')
        : t('storyBible.expandError', 'Falha ao expandir com IA. Tente novamente.'));
    } finally {
      setExpanding(false);
    }
  };

  // Reset form when entry changes
  useEffect(() => {
    if (open) {
      setType(entry?.type ?? defaultType);
      setName(entry?.name ?? '');
      setContent(entry?.content ?? '');
      setAttributes(entry?.attributes ?? {});
      setAttrKey('');
      setAttrVal('');
    }
  }, [open, entry, defaultType]);

  const handleAddAttr = () => {
    const k = attrKey.trim();
    const v = attrVal.trim();
    if (!k || !v) return;
    setAttributes((prev) => ({ ...prev, [k]: v }));
    setAttrKey('');
    setAttrVal('');
  };

  const handleRemoveAttr = (key: string) => {
    setAttributes((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    try {
      await onSave({
        type,
        name: name.trim(),
        content: content.trim(),
        attributes: Object.keys(attributes).length > 0 ? attributes : null,
      });
      onOpenChange(false);
    } catch {
      // toast already shown by caller
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!entry;

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent className="sm:max-w-xl">
        <form onSubmit={handleSubmit}>
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {isEditing ? t('storyBible.editEntry', 'Editar entrada') : t('storyBible.newEntry', 'Nova entrada')}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {t('storyBible.entryModalDescription', 'Defina as informações do elemento da sua história.')}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <div className="grid gap-4 py-4">
            {/* Type */}
            <div className="grid gap-1.5">
              <Label htmlFor="entry-type">{t('storyBible.typeLabel', 'Tipo')}</Label>
              <Select value={type} onValueChange={(v) => setType(v as BibleEntryType)}>
                <SelectTrigger id="entry-type">
                  <SelectValue placeholder={t('storyBible.typeLabel', 'Tipo')} />
                </SelectTrigger>
                <SelectContent>
                  {ALL_TYPES.map((typeOption) => {
                    const Icon = ENTRY_TYPE_ICONS[typeOption];
                    return (
                      <SelectItem key={typeOption} value={typeOption}>
                        <span className="flex items-center gap-2">
                          <Icon size={14} />
                          {getEntryTypeLabel(t, typeOption)}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Name */}
            <div className="grid gap-1.5">
              <Label htmlFor="entry-name">{t('storyBible.nameLabel', 'Nome *')}</Label>
              <Input
                id="entry-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('storyBible.namePlaceholder', 'Nome do personagem, local, etc.')}
                required
              />
            </div>

            {/* Content / description */}
            <div className="grid gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="entry-content">{t('storyBible.descriptionLabel', 'Descrição')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleExpand} disabled={expanding || !name.trim()}
                  className="h-7 gap-1.5 px-2 text-xs">
                  {expanding ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  {t('storyBible.expandWithAi', 'Criar/expandir com IA')}
                </Button>
              </div>
              <Textarea
                id="entry-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t('storyBible.descriptionPlaceholder', 'Escreva uma semente (ex: “jovem rico que herdou uma fortuna e luta contra a corrupção”) e clique em Criar/expandir com IA para a IA aprofundar.')}
                rows={5}
                className="resize-none"
              />
            </div>

            {/* Attributes */}
            <div className="grid gap-2">
              <Label>{t('storyBible.attributesLabel', 'Atributos (chave / valor)')}</Label>
              {Object.entries(attributes).length > 0 && (
                <div className="space-y-1">
                  {Object.entries(attributes).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 rounded-lg bg-muted px-3 py-1.5 text-sm">
                      <span className="font-medium min-w-0 truncate">{k}</span>
                      <ChevronRight size={12} className="text-muted-foreground shrink-0" />
                      <span className="min-w-0 flex-1 truncate text-muted-foreground">{v}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttr(k)}
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder={t('storyBible.attrKeyPlaceholder', 'Chave (ex: idade)')}
                  value={attrKey}
                  onChange={(e) => setAttrKey(e.target.value)}
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAttr();
                    }
                  }}
                />
                <Input
                  placeholder={t('storyBible.attrValuePlaceholder', 'Valor (ex: 30)')}
                  value={attrVal}
                  onChange={(e) => setAttrVal(e.target.value)}
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAttr();
                    }
                  }}
                />
                <Button type="button" variant="outline" size="icon" onClick={handleAddAttr}>
                  <Plus size={16} />
                </Button>
              </div>
            </div>
          </div>

          <ResponsiveModalFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('storyBible.cancel', 'Cancelar')}
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
              {isEditing ? t('storyBible.saveChanges', 'Salvar alterações') : t('storyBible.createEntry', 'Criar entrada')}
            </Button>
          </ResponsiveModalFooter>
        </form>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

// ============================================
// EntryCard
// ============================================

interface EntryCardProps {
  entry: BibleEntry;
  onEdit: (entry: BibleEntry) => void;
  onDelete: (entry: BibleEntry) => void;
  /** Abre a ficha-dossiê da entrada. */
  onOpen: (entry: BibleEntry) => void;
  /** Abre o estúdio de imagem (gerar/trocar) — gerido no nível da lista. */
  onEditImage: (entry: BibleEntry) => void;
}

function EntryCard({ entry, onEdit, onDelete, onOpen, onEditImage }: EntryCardProps) {
  const { t } = useTranslation();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(entry)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(entry); }}
      className="group relative flex gap-3.5 rounded-[14px] bg-card/70 p-4 shadow-[0_0_0_1px_var(--border)_inset] cursor-pointer transition-colors hover:bg-card"
    >
      {/* uni-av — foto real quando houver, senão monograma gradiente */}
      {entry.imageUrl ? (
        <img src={entry.imageUrl} alt={entry.name} className="h-10 w-10 shrink-0 rounded-full object-cover" />
      ) : (
        <span
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full font-serif text-[16px] font-semibold text-[#070B16]"
          style={{ background: TYPE_GRADIENT[entry.type] ?? TYPE_GRADIENT.note }}
        >
          {entry.name.charAt(0).toUpperCase()}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <h3 className="font-serif text-[15px] font-medium leading-tight text-foreground">
          <span className="align-middle">{entry.name}</span>
          <span className={cn('ml-2 inline-block align-middle rounded-full px-2 py-[3px] font-mono text-[7.5px] uppercase tracking-[0.16em] ring-1 ring-inset', TYPE_CHIP[entry.type] ?? TYPE_CHIP.note)}>
            {getEntryTypeLabel(t, entry.type)}
          </span>
        </h3>
        {entry.content && (
          <p className="mt-1 text-[11px] leading-[1.5] text-muted-foreground line-clamp-2">{entry.content}</p>
        )}
        {entry.attributes && Object.keys(entry.attributes).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {Object.entries(entry.attributes).slice(0, 4).map(([k, v]) => (
              <span key={k} className="rounded-full px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.08em] text-muted-foreground ring-1 ring-inset ring-border">
                {k}: {v}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ações — hover; não propagam o clique da ficha */}
      <div
        className="absolute right-2 top-2 flex items-center gap-1 rounded-lg bg-card/80 backdrop-blur-sm opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => onEditImage(entry)}
          className="p-1.5 rounded-lg text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
          title={t('storyBible.imageStudioTitle', 'Imagem (gerar / trocar)')}
        >
          <ImageIcon size={13} />
        </button>
        <button
          onClick={() => onEdit(entry)}
          className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          title={t('storyBible.editTitle', 'Editar')}
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={() => onDelete(entry)}
          className="p-1.5 rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
          title={t('storyBible.deleteTitle', 'Excluir')}
        >
          <Trash2 size={13} />
        </button>
      </div>

    </div>
  );
}

// ============================================
// StoryBibleView — main component
// ============================================

export function StoryBibleView({ projectId: embeddedProjectId }: { projectId?: string } = {}) {
  const { t } = useTranslation();
  const { projectId: paramProjectId } = useParams<{ projectId: string }>();
  const projectId = embeddedProjectId ?? paramProjectId;
  const embedded = !!embeddedProjectId;
  const navigate = useNavigate();
  const { projects } = useAppStore();

  const [entries, setEntries] = useState<BibleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [autofilling, setAutofilling] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [activeType, setActiveType] = useState<BibleEntryType | 'all'>('all');
  const [dossierEntry, setDossierEntry] = useState<BibleEntry | null>(null);
  // Image studio lifted to the list level (single instance) — opened from a card
  // action or from inside the dossier. studioEntry is retained across close for
  // the exit animation.
  const [studioEntry, setStudioEntry] = useState<BibleEntry | null>(null);
  const [studioOpen, setStudioOpen] = useState(false);
  const openStudio = useCallback((e: BibleEntry) => { setStudioEntry(e); setStudioOpen(true); }, []);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<BibleEntry | null>(null);
  const [defaultModalType, setDefaultModalType] = useState<BibleEntryType>('character');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<BibleEntry | null>(null);

  // Project-wide "Estilo visual" (projects.mangaStyle) — applied server-side to
  // every AI image generation in the project (bible sheets, illustrations, cover, manga).
  const [stylePopoverOpen, setStylePopoverOpen] = useState(false);
  const [projectStyle, setProjectStyle] = useState('');
  const [savingStyle, setSavingStyle] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    api.getProject(projectId)
      .then((res) => setProjectStyle(res.project.mangaStyle ?? ''))
      .catch(() => {
        // Non-fatal — the picker just starts empty
      });
  }, [projectId]);

  const handleSaveStyle = async () => {
    if (!projectId) return;
    setSavingStyle(true);
    try {
      await api.updateProject(projectId, { mangaStyle: projectStyle.trim() || null });
      toast.success(t('style.saved', 'Estilo visual salvo.'));
      setStylePopoverOpen(false);
    } catch {
      toast.error(t('style.saveError', 'Falha ao salvar o estilo visual.'));
    } finally {
      setSavingStyle(false);
    }
  };

  const fetchEntries = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.getBibleEntries(projectId);
      setEntries(res.entries);
    } catch {
      toast.error(t('storyBible.loadBibleError', 'Falha ao carregar o Universo.'));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Open create modal
  const handleAdd = (type?: BibleEntryType) => {
    setEditingEntry(null);
    setDefaultModalType(type ?? 'character');
    setModalOpen(true);
  };

  // Open edit modal
  const handleEdit = (entry: BibleEntry) => {
    setEditingEntry(entry);
    setModalOpen(true);
  };

  // Confirm delete
  const handleDeleteRequest = (entry: BibleEntry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectId || !entryToDelete) return;
    try {
      await api.deleteBibleEntry(projectId, entryToDelete.id);
      setEntries((prev) => prev.filter((e) => e.id !== entryToDelete.id));
      // cerimônia: o nome é riscado e a ficha recebe o carimbo REMOVIDO
      window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'burn', name: entryToDelete.name } }));
      toast.success(t('storyBible.entryRemoved', { name: entryToDelete.name, defaultValue: '"{{name}}" removido.' }));
    } catch {
      toast.error(t('storyBible.deleteEntryError', 'Falha ao excluir a entrada.'));
    } finally {
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
    }
  };

  // Save (create or update)
  const handleSave = async (data: CreateBibleEntryData) => {
    if (!projectId) return;
    try {
      if (editingEntry) {
        const res = await api.updateBibleEntry(projectId, editingEntry.id, data);
        setEntries((prev) => prev.map((e) => (e.id === editingEntry.id ? res.entry : e)));
        toast.success(t('storyBible.entryUpdated', 'Entrada atualizada.'));
      } else {
        const res = await api.createBibleEntry(projectId, {
          ...data,
          sortOrder: entries.length,
        });
        setEntries((prev) => [...prev, res.entry]);
        // cerimônias: a 10ª alma ganha o anel; as demais, a ficha ARQUIVADO
        const soulCount = entries.length + 1;
        const SOUL_MILESTONES = [5, 20, 30, 40, 50, 100, 150, 200, 300, 400, 500, 600, 700, 800, 900];
        if (soulCount === 1000) {
          // o quase impossível
          window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'thousand' } }));
        } else if (soulCount === 10) {
          window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'tensouls' } }));
        } else if (SOUL_MILESTONES.includes(soulCount)) {
          window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'archivemilestone', count: soulCount } }));
        } else {
          const kinds = new Set([...entries.map((e) => e.type), res.entry.type]);
          const cartoKey = `moment-carto-${projectId}`;
          if (kinds.size >= 4 && !localStorage.getItem(cartoKey)) {
            localStorage.setItem(cartoKey, '1');
            window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'cartographer' } }));
          } else {
            window.dispatchEvent(new CustomEvent('reformu:moment', {
              detail: { type: 'file', name: res.entry.name, label: getEntryTypeLabel(t, res.entry.type) },
            }));
          }
        }
      }
    } catch {
      toast.error(t('storyBible.saveEntryError', 'Falha ao salvar a entrada.'));
      throw new Error('save failed');
    }
  };

  // Reflect an entry updated in-place (e.g. after generating/uploading an image)
  const handleEntryUpdated = useCallback((updated: BibleEntry) => {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    // cerimônia: a primeira foto de uma ficha — o rosto sai da névoa (1x na vida)
    if (updated.imageUrl && !localStorage.getItem('moment-reveal')) {
      localStorage.setItem('moment-reveal', '1');
      window.dispatchEvent(new CustomEvent('reformu:moment', {
        detail: { type: 'reveal', name: updated.name, img: updated.imageUrl },
      }));
    }
  }, []);

  // Autofill from AI. force=true rebuilds the bible from the premise/outline,
  // bypassing the 6h cache (used by "Regerar").
  const handleAutofill = async (force = false) => {
    if (!projectId) return;
    setAutofilling(true);
    try {
      const res = await api.autofillBible(projectId, force);
      if (res.inserted === 0) {
        toast(force
          ? t('storyBible.bibleUpToDateForced', 'A Bible já reflete a premissa/conteúdo atual.')
          : t('storyBible.bibleUpToDate', 'Nenhuma entrada nova encontrada. A Bible já está atualizada.'));
      } else {
        toast.success(t('storyBible.entriesAddedByAi', { count: res.inserted, defaultValue: '{{count}} entrada(s) adicionada(s) pela IA.' }));
        await fetchEntries();
      }
    } catch {
      toast.error(t('storyBible.autofillError', 'Falha ao preencher com IA. Tente novamente.'));
    } finally {
      setAutofilling(false);
    }
  };

  // Scan the project's written chapters for named characters not yet in the
  // Universe and add them. Complements handleAutofill (which draws from the
  // premise/outline) by mining the actual prose.
  const handleDetectCharacters = async () => {
    if (!projectId) return;
    setDetecting(true);
    try {
      const { chapters } = await api.getChapters(projectId).catch(() => ({ chapters: [] }));
      let text = chapters
        .map((c) => (c.content ?? '').replace(/<[^>]+>/g, ' '))
        .join('\n\n')
        .replace(/\s+/g, ' ')
        .trim();

      // Manga (or an empty book) has no chapters — its story text lives in the
      // page BUBBLES. Mine those + the premise so detection works there too.
      if (text.length < 40) {
        const [pagesRes, projRes] = await Promise.all([
          api.getMangaPages(projectId).catch(() => ({ pages: [] as MangaPage[] })),
          api.getProject(projectId).catch(() => null),
        ]);
        const bubbleText = pagesRes.pages
          .map((pg) => (pg.layout?.bubbles ?? []).map((b) => b.text).join(' '))
          .join('\n');
        text = [text, bubbleText, projRes?.project.description ?? '']
          .join('\n')
          .replace(/\s+/g, ' ')
          .trim();
      }
      text = text.slice(0, 12000);

      if (!text) {
        toast(t('storyBible.detectNoText', 'Nenhum texto ainda — escreva capítulos ou balões primeiro.'));
        return;
      }

      const existingNames = entries.map((e) => e.name);
      const { characters } = await api.extractCharacters(text, existingNames, projectId);

      const existingLower = new Set(existingNames.map((n) => n.toLowerCase()));
      const toCreate = characters.filter((c) => c.name.trim() && !existingLower.has(c.name.trim().toLowerCase()));

      let added = 0;
      for (const character of toCreate) {
        try {
          await api.createBibleEntry(projectId, {
            type: 'character',
            name: character.name.trim(),
            content: character.description ?? '',
          });
          added++;
        } catch {
          // skip this one, keep going
        }
      }

      if (added > 0) {
        await fetchEntries();
        toast.success(t('storyBible.detectAdded', { count: added, defaultValue: '{{count}} personagem(ns) adicionado(s) ao Universo.' }));
      } else {
        toast(t('storyBible.detectNoneNew', 'Nenhum personagem novo encontrado.'));
      }
    } catch (e) {
      // Surface the REAL cause — a generic "failed" hides what to fix.
      console.error('[detectCharacters] failed:', e);
      const detail = e instanceof Error ? e.message : (e as { message?: string })?.message;
      toast.error(e instanceof AiLimitError
        ? t('storyBible.expandLimit', 'Limite de ações de IA atingido este mês.')
        : `${t('storyBible.detectError', 'Falha ao detectar personagens. Tente novamente.')}${detail ? ` (${detail})` : ''}`);
    } finally {
      setDetecting(false);
    }
  };

  // "Estilo visual" popover trigger — shared between the standalone header
  // actions and the embedded (editor tab) compact action bar.
  const renderStyleButton = (compact = false) => (
    <Popover open={stylePopoverOpen} onOpenChange={setStylePopoverOpen}>
      <PopoverTrigger
        title={t('style.title', 'Estilo visual')}
        aria-label={t('style.title', 'Estilo visual')}
        className="btn-ghost-mock inline-flex items-center gap-1.5 shrink-0"
        style={{ padding: compact ? '8px 14px' : '8px 12px', fontSize: '9px' }}
      >
        <Palette size={14} />
        {!compact && <span className="hidden sm:inline">{t('style.title', 'Estilo visual')}</span>}
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-80 space-y-3 p-4">
        <div>
          <p className="text-sm font-medium text-foreground">{t('style.title', 'Estilo visual')}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('style.hint', 'Este estilo é aplicado a todas as ilustrações e personagens gerados.')}
          </p>
        </div>
        <StylePicker value={projectStyle} onChange={setProjectStyle} disabled={savingStyle} />
        <div className="flex justify-end pt-1">
          <Button size="sm" onClick={handleSaveStyle} disabled={savingStyle} className="gap-1.5">
            {savingStyle && <Loader2 size={13} className="animate-spin" />}
            {t('common.save', 'Salvar')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );

  // Shared header actions — identical on desktop and mobile, standalone and
  // embedded. One "Adicionar", one "Estilo", and a single "IA" menu that groups
  // the two AI ways to populate the Universe (from the premise vs. from the
  // written text) so they're no longer two confusing top-level buttons.
  const aiBusy = autofilling || detecting;
  const renderActions = (compact: boolean) => (
    <>
      <button
        type="button"
        onClick={() => handleAdd()}
        className="btn-ghost-mock inline-flex items-center gap-1.5 shrink-0"
        style={{ padding: compact ? '8px 14px' : '8px 12px', fontSize: '9px' }}
      >
        <Plus size={14} />
        {t('storyBible.add', 'Adicionar')}
      </button>
      {renderStyleButton(compact)}
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={aiBusy}
          className="btn-ember shrink-0 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ padding: compact ? '8px 16px' : '8px 14px', fontSize: '9.5px' }}
        >
          {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {t('storyBible.aiMenu', 'IA')}
          <ChevronDown size={13} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[280px]">
          <DropdownMenuGroup>
            <DropdownMenuLabel>{t('storyBible.aiMenuLabel', 'Popular o Universo com IA')}</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleAutofill(false)} disabled={aiBusy} className="flex-col items-start gap-0.5 py-2">
              <span className="flex items-center gap-2 font-medium">
                <Sparkles size={13} /> {t('storyBible.fillWithAi', 'Preencher com IA')}
              </span>
              <span className="pl-5 text-xs text-muted-foreground">
                {t('storyBible.fillWithAiDesc', 'Cria personagens e lore a partir da premissa e do roteiro.')}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDetectCharacters} disabled={aiBusy} className="flex-col items-start gap-0.5 py-2">
              <span className="flex items-center gap-2 font-medium">
                <ScanText size={13} /> {t('storyBible.detectCharacters', 'Detectar personagens do texto')}
              </span>
              <span className="pl-5 text-xs text-muted-foreground">
                {t('storyBible.detectCharactersDesc', 'Varre os capítulos já escritos e cataloga quem falta.')}
              </span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  // Filter entries by active type
  const filteredEntries = activeType === 'all'
    ? entries
    : entries.filter((e) => e.type === activeType);

  // Group for display when showing all
  const grouped = ALL_TYPES.reduce<Record<BibleEntryType, BibleEntry[]>>(
    (acc, entryType) => {
      acc[entryType] = entries.filter((e) => e.type === entryType);
      return acc;
    },
    {} as Record<BibleEntryType, BibleEntry[]>
  );

  const countByType = (type: BibleEntryType) => entries.filter((e) => e.type === type).length;

  // ── No book chosen yet → show a picker instead of auto-opening "the last book".
  if (!projectId) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex-shrink-0 bg-card px-6 pt-4 border-b border-border">
          <PageHeader
            icon={<BookMarked size={20} />}
            title={t('storyBible.bookUniverseTitle', 'Universo do Livro')}
            subtitle={t('storyBible.chooseBookSubtitle', 'Escolha um livro para começar')}
            className="border-b-0 mb-0 md:mb-0"
          />
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {projects.length === 0 ? (
            <EmptyState
              icon={<BookMarked size={36} />}
              title={t('storyBible.noBooksYetTitle', 'Nenhum livro na estante ainda.')}
              description={t('storyBible.noBooksYetDescription', 'Crie um livro primeiro — o Universo do Livro vive dentro dele.')}
              action={
                <Button onClick={() => navigate('/app/projects')} className="gap-2">
                  <Plus size={14} />
                  {t('storyBible.createFirstBook', 'Criar meu primeiro livro')}
                </Button>
              }
              className="h-64 py-0"
            />
          ) : (
            <div className="mx-auto max-w-xl">
              {/* Single column, each row showing the book's actual cover */}
              <div className="grid grid-cols-1 gap-2.5">
                {projects.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/app/bible/${p.id}`)}
                    className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-3 text-left transition-all hover:border-primary/40 hover:shadow-md"
                  >
                    <div className="h-14 w-10 shrink-0 overflow-hidden rounded-md shadow-sm ring-1 ring-border">
                      <BookCover
                        title={p.metadata.title || t('storyBible.untitled', 'Sem título')}
                        author={p.metadata.author || ''}
                        genre={p.metadata.genre || ''}
                        coverImage={p.metadata.coverImage}
                        variant="cover"
                        seed={p.id}
                        progress={0}
                        disableTilt
                        className="h-full w-full"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-foreground truncate">
                        {p.metadata.title || t('storyBible.untitled', 'Sem título')}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {p.metadata.author || t('storyBible.noAuthor', 'Sem autor')}
                      </p>
                    </div>
                    <ChevronRight
                      size={16}
                      className="text-muted-foreground shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header — only standalone. Embedded in the editor workspace, the shared
          tab bar (Editor · Universo · Estrutura) is the header; here we drop the
          duplicate title and relocate the actions to a slim bar below. */}
      {!embedded && (
      <div className="flex-shrink-0 bg-card px-6 pt-4 border-b border-border">
        <PageHeader
          icon={<BookMarked size={20} />}
          title={t('storyBible.bookUniverseTitle', 'Universo do Livro')}
          subtitle={t('storyBible.bookUniverseSubtitle', 'Personagens, locais e lore')}
          className="border-b-0 mb-0 md:mb-0"
          actions={
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              {/* Book switcher — non-embedded only */}
              {!embedded && (
                <Select value={projectId} onValueChange={(id) => navigate(`/app/bible/${id}`)}>
                  <SelectTrigger className="h-8 w-auto max-w-[160px] gap-1 rounded-lg border border-border bg-muted/40 px-2 py-0 text-xs font-medium text-foreground focus:ring-0 shrink-0">
                    <span className="truncate">
                      {projects.find((p) => p.id === projectId)?.metadata.title || t('storyBible.chooseBook', 'Escolher livro')}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.metadata.title || t('storyBible.untitled', 'Sem título')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {renderActions(false)}
            </div>
          }
        />
      </div>
      )}

      {/* Embedded action bar — mirrors the header actions without a duplicate title.
          Linguagem do app: ações secundárias em btn-ghost-mock, ação primária
          (gerar com IA) em btn-ember. Compacto no mobile (ícone + rótulo curto). */}
      {embedded && (
        <div className="flex-shrink-0 flex flex-wrap items-center gap-2 px-4 pt-3">
          {renderActions(true)}
        </div>
      )}

      {/* Type filter pills */}
      <div className="flex-shrink-0 flex items-center gap-2 px-6 py-3 border-b border-border overflow-x-auto scrollbar-hide">
        <button
          onClick={() => setActiveType('all')}
          className={cn(
            'shrink-0 rounded-full px-3.5 py-[7px] font-mono text-[9px] uppercase tracking-[0.14em] transition-colors ring-1 ring-inset',
            activeType === 'all'
              ? 'text-[color:var(--ember-hot)] bg-[rgba(223,201,147,0.08)] ring-[rgba(223,201,147,0.35)]'
              : 'ring-border text-muted-foreground hover:text-foreground'
          )}
        >
          {t('storyBible.allCount', { count: entries.length, defaultValue: 'Todos ({{count}})' })}
        </button>
        {ALL_TYPES.map((type) => {
          const Icon = ENTRY_TYPE_ICONS[type];
          const count = countByType(type);
          return (
            <button
              key={type}
              onClick={() => setActiveType(type)}
              className={cn(
                'shrink-0 flex items-center gap-1.5 rounded-full px-3.5 py-[7px] font-mono text-[9px] uppercase tracking-[0.14em] transition-colors ring-1 ring-inset',
                activeType === type
                  ? 'text-[color:var(--ember-hot)] bg-[rgba(223,201,147,0.08)] ring-[rgba(223,201,147,0.35)]'
                  : 'ring-border text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon size={11} />
              {getEntryTypeLabel(t, type)} ({count})
            </button>
          );
        })}
      </div>

      {/* Content — pb-24 clears the bottom tab bar on mobile */}
      <div className="flex-1 overflow-y-auto px-6 py-4 pb-24">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <Loader2 size={20} className="animate-spin text-muted-foreground/50" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/50">
              {t('storyBible.loadingBible', 'Carregando o Universo do Livro…')}
            </p>
          </div>
        ) : entries.length === 0 ? (
          /* Empty state */
          <EmptyState
            icon={<BookMarked size={36} />}
            eyebrow={t('storyBible.universeEyebrow', 'Universo')}
            title={t('storyBible.emptyEntriesTitle', 'Seus personagens ainda não têm nome.')}
            description={t('storyBible.emptyEntriesDescription', 'Adicione personagens, locais e lore que a IA deve respeitar ao escrever. Ou deixe a IA preencher a partir do que você já escreveu.')}
            action={
              <div className="flex flex-col sm:flex-row gap-3 items-center">
                <Button
                  variant="outline"
                  onClick={() => handleAutofill(false)}
                  disabled={autofilling}
                  className="gap-2"
                >
                  {autofilling ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {t('storyBible.fillWithAi', 'Preencher com IA')}
                </Button>
                <Button onClick={() => handleAdd()} className="gap-2">
                  <Plus size={14} />
                  {t('storyBible.addFirstCharacter', 'Adicionar o primeiro personagem')}
                </Button>
              </div>
            }
            className="h-64 py-0"
          />
        ) : activeType === 'all' ? (
          /* Grouped view */
          <div className="space-y-8">
            {ALL_TYPES.map((type) => {
              const typeEntries = grouped[type];
              if (typeEntries.length === 0) return null;
              const Icon = ENTRY_TYPE_ICONS[type];
              return (
                <section key={type}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon size={16} className="text-muted-foreground" />
                      <h2 className="font-semibold text-foreground text-sm uppercase tracking-wider font-mono">
                        {getEntryTypeLabel(t, type)}
                        <span className="ml-2 text-muted-foreground font-normal">({typeEntries.length})</span>
                      </h2>
                    </div>
                    <button
                      onClick={() => handleAdd(type)}
                      className="grid h-6 w-6 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                      title={t('storyBible.addEntryType', { type: getEntryTypeLabel(t, type), defaultValue: 'Adicionar {{type}}' })}
                      aria-label={t('storyBible.addEntryType', { type: getEntryTypeLabel(t, type), defaultValue: 'Adicionar {{type}}' })}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {typeEntries.map((entry, riseIdx) => (
                      <Rise i={riseIdx} key={entry.id}>
                      <EntryCard
                        onOpen={setDossierEntry}
                        entry={entry}
                        onEdit={handleEdit}
                        onDelete={handleDeleteRequest}
                        onEditImage={openStudio}
                      />
                      </Rise>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
          /* Filtered view */
          filteredEntries.length === 0 ? (
            <EmptyState
              title={t('storyBible.noEntryTypeTitle', { type: getEntryTypeLabel(t, activeType).toLowerCase(), defaultValue: 'Nenhum {{type}} cadastrado.' })}
              description={t('storyBible.noEntryTypeDescription', 'Adicione manualmente ou deixe a IA detectar a partir do texto já escrito.')}
              action={
                <Button size="sm" variant="outline" onClick={() => handleAdd(activeType)} className="gap-2">
                  <Plus size={14} />
                  {t('storyBible.addEntryType', { type: getEntryTypeLabel(t, activeType), defaultValue: 'Adicionar {{type}}' })}
                </Button>
              }
              className="h-48 py-0"
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredEntries.map((entry, riseIdx) => (
                <Rise i={riseIdx} key={entry.id}>
                <EntryCard
                  onOpen={setDossierEntry}
                  entry={entry}
                  onEdit={handleEdit}
                  onDelete={handleDeleteRequest}
                  onEditImage={openStudio}
                />
                </Rise>
              ))}
            </div>
          )
        )}
      </div>

      {/* Entry Modal */}
      {/* Ficha-dossiê cinematográfica */}
      <EntryDossier
        entry={dossierEntry}
        typeLabel={(e) => getEntryTypeLabel(t, e.type)}
        onClose={() => setDossierEntry(null)}
        onEdit={(e) => { setDossierEntry(null); handleEdit(e); }}
        onDelete={(e) => { setDossierEntry(null); handleDeleteRequest(e); }}
        onEditImage={(e) => { setDossierEntry(null); openStudio(e); }}
      />

      {/* Image studio — single instance for the whole list; opened from a card
          action or from the dossier's photo. */}
      {studioEntry && projectId && (
        <EntryImageStudio
          open={studioOpen}
          onOpenChange={setStudioOpen}
          projectId={projectId}
          entry={studioEntry}
          onUpdated={(u) => {
            handleEntryUpdated(u);
            setStudioEntry(u);
            setDossierEntry((d) => (d && d.id === u.id ? u : d));
          }}
        />
      )}

      <EntryModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        entry={editingEntry}
        defaultType={defaultModalType}
        projectId={projectId}
        onSave={handleSave}
        onExternalEntriesAdded={() => fetchEntries()}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('storyBible.deleteEntryTitle', 'Excluir entrada')}</DialogTitle>
            <DialogDescription>
              {t('storyBible.deleteConfirmPrefix', 'Tem certeza que deseja excluir')}{' '}
              <strong>"{entryToDelete?.name}"</strong>{t('storyBible.deleteConfirmSuffix', '? Esta ação não pode ser desfeita.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('storyBible.cancel', 'Cancelar')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
            >
              {t('storyBible.delete', 'Excluir')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
